import React from 'react';

import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { getSiteTitle, IntegrationStepWrapper, SpaceIcon } from '../../../../Components';
import { getSiteIcon } from '../../../../Components/Site/SiteIcon';
import { useSetAnalyticsScreen } from '../../../../navigation';
import type { IntegrationInstallationFlowProps, StepContextType } from '../types';

type PickContentToConfigureProps = IntegrationInstallationFlowProps & {
    context: StepContextType<'pick_content_to_configure'>;
    onNext: (params: StepContextType<'configure_content_installation'>) => void;
};

/**
 * Step to pick a specific content  (site or space) to configure
 */
export function PickContentToConfigureStep(props: PickContentToConfigureProps) {
    const { context, onNext, installation, integration, organizationId } = props;

    const anchorRef = React.useRef<HTMLButtonElement | null>(null);
    const palette = usePaletteHandle(anchorRef);

    const onSelection = useEventCallback(() => {
        palette.open(createPickContentToConfigurePalette({ context, onNext }));
    });

    useSetAnalyticsScreen({
        screen: 'integration_installation',
        step: 'pick_content_to_configure',
        integration: integration.name,
        organization: organizationId,
        context: context.type,
        withAccountInstallation: !!installation,
    });

    return (
        <IntegrationStepWrapper
            title={
                props.context.type === 'space'
                    ? 'Pick a space to configure'
                    : 'Pick a site to configure'
            }
            description={`This integration requires you to configure it in a specific ${props.context.type}`}
        >
            <Button
                kind="secondary"
                label={context.type === 'space' ? 'Choose space' : 'Choose site'}
                trailingIcon={Icon.ChevronDown}
                onPress={onSelection}
                active={palette.opened}
                ref={anchorRef}
            />
        </IntegrationStepWrapper>
    );
}

/**
 * Utility function to create the palette to select content to install an integration
 */
function createPickContentToConfigurePalette(
    props: Pick<PickContentToConfigureProps, 'context' | 'onNext'>
) {
    const { context, onNext: onPick } = props;

    return createPalette({
        search: {
            placeholder:
                context.type === 'space'
                    ? 'Search for spaces to configure'
                    : 'Search for sites to configure',
        },
        groups: [
            {
                key: `${context.type}-to-configure`,
                showCommandsWhileComputing: false,
                commands: (context.type === 'space'
                    ? context.installations.map((installation) => {
                          assert(
                              typeof installation.space === 'object',
                              'Expected space to be an object'
                          );
                          const { space } = installation;

                          return {
                              key: space.id,
                              title: space.title,
                              icon: <SpaceIcon space={space} />,
                              trigger: () => onPick({ type: context.type, installation }),
                          };
                      })
                    : context.installations.map((installation) => {
                          assert(
                              typeof installation.site === 'object',
                              'Expected space to be an object'
                          );
                          const { site } = installation;

                          return {
                              key: site.id,
                              title: getSiteTitle(site.title),
                              icon: getSiteIcon(site),
                              trigger: () => onPick({ type: context.type, installation }),
                          };
                      })
                ).filter(filterOutNullable),
            },
        ],
    });
}
